import type { ManualEntryContext, ManualEntryItem, ManualEntryParseResult } from "../types/manualEntry";

export const STAY_ENTRY_ROLES = ["学生", "老师", "导游", "司机"] as const;
export type StayEntryRole = typeof STAY_ENTRY_ROLES[number];
export type StayEntryTableRow = {
  source: ManualEntryItem["source"]; role: string; standard: string; people: string;
  nights: string; price: string; linked: boolean; editable: boolean; issues: string[];
};
export type StayEntryTableField = "role" | "standard" | "people" | "nights" | "price";
const roleKeys = { 学生: "studentCount", 老师: "teacherCount", 导游: "guideCount", 司机: "driverCount" } as const;
/** 读取角色统一人数供表格显示，不改变原有联动。 */
function roleCount(role: string, context: ManualEntryContext): string {
  return role in roleKeys ? String(context.people[roleKeys[role as StayEntryRole]] ?? 0) : "";
}
const valueOf = (value: string | undefined) => !value || /^(人数|晚数|单价|住宿标准|角色)$/.test(value) ? "" : value;

/** 表格是文本草稿的视图，读取时不重写原文，也不丢弃尚未填完的行。 */
export function stayEntryTableRows(preview: ManualEntryParseResult, context: ManualEntryContext): StayEntryTableRow[] {
  return preview.items.map(item => {
    const text = item.source.text.normalize("NFKC").trim();
    const roleMatch = text.match(/^(学生|老师|导游|司机|角色)\s*/u);
    const role = valueOf(roleMatch?.[1]), rest = text.slice(roleMatch?.[0].length ?? 0).trim();
    const matched = rest.match(/^(.+?)\s+([+\-.\d人数晚数][\s\S]*)$/u) ?? rest.match(/^(.+)\s+(\S+)$/u);
    const factors = (matched?.[2] ?? "").replace(/\s/gu, "").split(/[xX×*]/u).map(value => value.replace(/(?<=\d)(?:人|晚|元)$/u, ""));
    const linked = factors.length !== 3;
    return {
      source: item.source, role, standard: valueOf(matched?.[1]), linked,
      people: linked ? roleCount(role, context) : valueOf(factors[0]),
      nights: valueOf(factors.at(-2)), price: valueOf(factors.at(-1)),
      editable: Boolean(roleMatch && matched && (factors.length === 2 || factors.length === 3)),
      issues: item.issues.map(issue => issue.message),
    };
  });
}

/** 合并区域标准与草稿中已填标准，供选择或新建。 */
export function stayEntryStandards(context: ManualEntryContext, rows: StayEntryTableRow[], role: string): string[] {
  const area = context.areas?.find(item => item.id === context.areaId);
  const code = ({ 学生: "student", 老师: "teacher", 导游: "guide", 司机: "driver" } as Record<string, string>)[role];
  return [...new Set([
    ...(area?.standards ?? []).filter(item => !item.role || item.role === role || item.role === code).map(item => item.name),
    ...rows.map(row => row.standard).filter(Boolean),
    ...(area ? [area.name + "住宿"] : []),
  ])];
}

/** 只替换当前文本片段，保留其他行、分号和空行；改人数转为本行固定人数。 */
export function changeStayEntryRow(text: string, row: StayEntryTableRow, field: StayEntryTableField, value: string, context: ManualEntryContext): string {
  if (!row.editable) return text;
  const next = { ...row, [field]: value };
  if (field === "role") { next.linked = true; next.people = roleCount(value, context); }
  if (field === "people") next.linked = false;
  const formula = [next.linked ? null : next.people || "人数", next.nights || "晚数", next.price || "单价"].filter(part => part !== null).join("×");
  const record = `${next.role || "角色"} ${next.standard || "住宿标准"} ${formula}`;
  return text.slice(0, row.source.start) + record + text.slice(row.source.end);
}

/** 按当前区域晚数新增学生待补价行。 */
export function appendStayEntryRow(text: string, context: ManualEntryContext, rows: StayEntryTableRow[]): string {
  const area = context.areas?.find(item => item.id === context.areaId);
  const standard = stayEntryStandards(context, rows, "学生")[0] ?? "住宿标准";
  const nights = area?.nights && area.nights > 0 ? String(area.nights) : "晚数";
  return `${text}${text && !text.endsWith("\n") ? "\n" : ""}学生 ${standard} ${nights}×单价`;
}

/** 按原文范围删除单条记录，连同相邻分隔符移除，避免误删同名或同角色的其他行。 */
export function removeStayEntryRow(text: string, row: StayEntryTableRow): string {
  let { start, end } = row.source;
  const after = text.slice(end).match(/^[\s;；]+/u)?.[0];
  if (after) end += after.length;
  else start -= text.slice(0, start).match(/[\s;；]+$/u)?.[0].length ?? 0;
  const next = text.slice(0, start) + text.slice(end);
  return /^[\s;；]*$/u.test(next) ? "" : next;
}
